import { Person, FamilyContext } from "../types";

// Obter a pessoa atual com base na seleção na árvore familiar
export const getCurrentPerson = (
  person: Person, 
  selection: FamilyContext
): Person | undefined => {
  const { 
    activeTab, 
    path
  } = selection;
  
  // Se estiver na aba de cônjuge, retornar o cônjuge
  if (activeTab === "COUPLE") {
    return person.couple?.[0];
  }
  
  // Se estiver na aba pessoal, retornar a própria pessoa
  if (activeTab === "PERSONAL") {
    return person;
  }
  
  // Se estiver na aba de filhos, navegar pela árvore
  if (activeTab === "CHILD") {
    // Navegar pela hierarquia familiar usando o caminho
    let currentPerson = person;
    let currentChildren = currentPerson.children;
    
    if (!currentChildren || currentChildren.length === 0) {
      return undefined;
    }
    
    // Para cada nível de profundidade no caminho
    for (let i = 0; i < path.length; i++) {
      const index = path[i];
      
      // Verificar se o índice é válido
      if (!currentChildren || index >= currentChildren.length) {
        return undefined;
      }
      
      // Acessar a pessoa neste nível
      const childPerson: Person = currentChildren[index][0];
      
      // Se este for o último nível, retornar esta pessoa
      if (i === path.length - 1) {
        return childPerson;
      }
      
      // Caso contrário, continuar navegando para o próximo nível
      currentChildren = childPerson.children;
    }
  }
  
  return undefined;
};

// Atualizar uma pessoa em um nível específico da hierarquia
export const updatePersonAtPath = (
  rootPerson: Person,
  path: number[],
  updatedPerson: Person
): Person => {
  // Caso base: caminho vazio, atualizar a pessoa raiz
  if (path.length === 0) {
    return updatedPerson;
  }
  
  // Fazer uma cópia de segurança
  const result = { ...rootPerson };
  
  // Navegar pela hierarquia
  let currentPerson: Person = result;
  let parentPerson: Person | null = null;
  
  for (let i = 0; i < path.length; i++) {
    const index = path[i];
    
    // Garantir que children existe
    if (!currentPerson.children) {
      currentPerson.children = [];
    }
    
    // Garantir que o índice existe
    if (index >= currentPerson.children.length) {
      // Preencher com arrays vazios até o índice necessário
      while (currentPerson.children.length <= index) {
        currentPerson.children.push([{}]);
      }
    }
    
    // Se este for o último nível, atualizar esta pessoa
    if (i === path.length - 1) {
      currentPerson.children[index] = [updatedPerson];
      break;
    }
    
    // Caso contrário, continuar navegando
    parentPerson = currentPerson;
    currentPerson = currentPerson.children[index][0];
    
    // Garantir que a pessoa atual existe
    if (!currentPerson) {
      currentPerson = {};
      parentPerson.children![index] = [currentPerson];
    }
  }
  
  return result;
};

// Obter labels baseados no nível atual
export const getLabelsByLevel = (level: string) => {
  switch (level) {
    case "MAIN":
      return {
        person: "Pessoa",
        personPlural: "Pessoas",
        couple: "Cônjuge",
        children: "Filhos",
        addPerson: "Adicionar Pessoa",
        personalData: "Dados Pessoais",
        personalDescription: "Informe seus dados pessoais"
      };
    case "CHILD":
      return {
        person: "Filho",
        personPlural: "Filhos",
        couple: "Cônjuge do Filho",
        children: "Netos",
        addPerson: "Adicionar Filho",
        personalData: "Dados do Filho",
        personalDescription: "Informe os dados do filho"
      };
    case "GRANDCHILD":
      return {
        person: "Neto",
        personPlural: "Netos",
        couple: "Cônjuge do Neto",
        children: "Bisnetos",
        addPerson: "Adicionar Neto",
        personalData: "Dados do Neto",
        personalDescription: "Informe os dados do neto"
      };
    case "GREATGRANDCHILD":
      return {
        person: "Bisneto",
        personPlural: "Bisnetos",
        couple: "Cônjuge do Bisneto",
        children: "",
        addPerson: "Adicionar Bisneto",
        personalData: "Dados do Bisneto",
        personalDescription: "Informe os dados do bisneto"
      };
    default:
      return {
        person: "Pessoa",
        personPlural: "Pessoas",
        couple: "Cônjuge",
        children: "Filhos",
        addPerson: "Adicionar Pessoa",
        personalData: "Dados Pessoais",
        personalDescription: "Informe seus dados"
      };
  }
}; 